import React, { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'reactstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { apiService } from '../../constants/ApiService';
import conditionalDataLoad from '../../utils/conditionalDataLoad';
import useVendorBranch from '../../utils/useVendorBranch';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const toStr = (v) => (v === null || v === undefined ? '' : String(v));
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const CourseContent = () => {
  const { user } = useSelector((state) => state.authState);
  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);

  const [errorMessage, setErrorMessage] = useState({});
  const [loading, setLoading] = useState(false);

  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [materials, setMaterials] = useState([]);

  const [expandedSem, setExpandedSem] = useState(false);
  const [expandedSub, setExpandedSub] = useState(false);
  const [expandedCh, setExpandedCh] = useState(false);

  // ✅ Weekend pattern: only vendor/branch selection
  const [selectionData, setSelectionData] = useState({
    vendor_id: '',
    branch_id: '',
  });

  const fetchContent = async () => {
    const vendorId = selectionData.vendor_id || '';
    const branchId = selectionData.branch_id || '';

    try {
      setLoading(true);

      // ✅ NO enrollmentId, NO semNo
      const res = await apiService.get(
        `api/BatchExamMaster/GetMyCourseContent?vendorId=${vendorId}&branchId=${branchId}`,
      );

      if (res?.status === true) {
        setSubjects(res?.subjects || []);
        setChapters(res?.chapters || []);
        setMaterials(res?.materials || []);

        const firstSem = (res?.subjects || [])?.[0]?.semester;
        setExpandedSem(firstSem ? `sem-${firstSem}` : false);
        setExpandedSub(false);
        setExpandedCh(false);

        if (res?.message) toast.info(res.message);
      } else {
        toast.error(res?.message || 'Failed');
        setSubjects([]);
        setChapters([]);
        setMaterials([]);
      }
    } catch (e) {
      console.error(e);
      toast.error('Something went wrong');
      setSubjects([]);
      setChapters([]);
      setMaterials([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const canLoad = conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: selectionData.vendor_id,
      branchId: selectionData.branch_id,
    });

    if (canLoad) fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionData.vendor_id, selectionData.branch_id]);

  // ---- Build Maps for fast join ----
  const chaptersBySubject = useMemo(() => {
    const map = new Map();
    (chapters || []).forEach((c) => {
      const sid = c?.subject_id;
      if (!sid) return;
      if (!map.has(sid)) map.set(sid, []);
      map.get(sid).push(c);
    });

    map.forEach((list) => {
      list.sort(
        (a, b) =>
          toNum(a?.chapter_order) - toNum(b?.chapter_order) ||
          toStr(a?.chapter_name).localeCompare(toStr(b?.chapter_name)),
      );
    });

    return map;
  }, [chapters]);

  const topicsByChapter = useMemo(() => {
    const map = new Map();
    (materials || []).forEach((t) => {
      const cid = t?.chapter_id;
      if (!cid) return;
      if (!map.has(cid)) map.set(cid, []);
      map.get(cid).push(t);
    });

    map.forEach((list) => {
      list.sort((a, b) =>
        toStr(a?.topic_name).localeCompare(toStr(b?.topic_name)),
      );
    });

    return map;
  }, [materials]);

  const semGroups = useMemo(() => {
    const map = new Map();
    (subjects || []).forEach((s) => {
      const sem = toStr(s?.semester || 'N/A');
      if (!map.has(sem)) map.set(sem, []);
      map.get(sem).push(s);
    });

    map.forEach((list) => {
      list.sort((a, b) =>
        toStr(a?.subject_name).localeCompare(toStr(b?.subject_name)),
      );
    });

    const entries = Array.from(map.entries());
    entries.sort((a, b) => toNum(a[0]) - toNum(b[0]));
    return entries;
  }, [subjects]);

  const totalChapters = chapters?.length || 0;
  const totalTopics = materials?.length || 0;

  return (
    <>
      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        errorMessage={errorMessage}
        accessButton={true}
      />

      <div className='container-fluid py-4'>
        <div className='card shadow-sm border-0'>
          <div className='card-header bg-white border-bottom py-3'>
            <div className='d-flex justify-content-between align-items-center'>
              <div>
                <h4 className='mb-1 fw-bold text-dark'>
                  <i className='bi bi-journal-text me-2' />
                  Course Content
                </h4>

                <div className='text-muted small'>
                  Subjects: <b>{subjects?.length || 0}</b> • Chapters:{' '}
                  <b>{totalChapters}</b> • Topics: <b>{totalTopics}</b>
                </div>
              </div>
            </div>
          </div>

          <div className='card-body p-4'>
            <hr className='my-2' />

            {loading ? (
              <div className='text-center py-5'>
                <Spinner /> <div className='mt-2 text-muted'>Loading...</div>
              </div>
            ) : semGroups.length === 0 ? (
              <div className='alert alert-light border text-center py-4'>
                <i className='bi bi-inbox fs-1 text-muted d-block mb-2'></i>
                <p className='text-muted mb-0'>No content found.</p>
              </div>
            ) : (
              <div>
                {semGroups.map(([sem, semSubjects]) => (
                  <Accordion
                    key={sem}
                    expanded={expandedSem === `sem-${sem}`}
                    onChange={(_, isExpanded) =>
                      setExpandedSem(isExpanded ? `sem-${sem}` : false)
                    }
                    className='mb-2'
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      className='bg-light'
                    >
                      <div className='d-flex align-items-center justify-content-between w-100 pe-2'>
                        <Typography className='fw-semibold'>
                          Semester {sem}
                        </Typography>
                        <Chip
                          label={`${semSubjects.length} Subject${semSubjects.length !== 1 ? 's' : ''}`}
                          size='small'
                          color='primary'
                        />
                      </div>
                    </AccordionSummary>

                    <AccordionDetails>
                      {semSubjects.map((sub, sIndex) => {
                        const subId = sub?.subject_id;
                        const subKey = `sub-${sem}-${subId || sIndex}`;
                        const subChapters = chaptersBySubject.get(subId) || [];

                        return (
                          <Accordion
                            key={subKey}
                            expanded={expandedSub === subKey}
                            onChange={(_, isExpanded) =>
                              setExpandedSub(isExpanded ? subKey : false)
                            }
                            className='mb-2'
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <div className='d-flex justify-content-between align-items-center w-100 pe-2'>
                                <div>
                                  <Typography className='fw-semibold'>
                                    {sub?.subject_name || 'Untitled Subject'}
                                  </Typography>
                                  <Typography
                                    variant='caption'
                                    className='text-muted'
                                  >
                                    SubjectId: {subId || 'N/A'} • Chapters:{' '}
                                    {subChapters.length}
                                  </Typography>
                                </div>

                                <div className='d-flex gap-2 align-items-center'>
                                  {sub?.syllabus_file ? (
                                    <a
                                      className='btn btn-sm btn-outline-primary'
                                      href={sub.syllabus_file}
                                      target='_blank'
                                      rel='noreferrer'
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <i className='bi bi-file-earmark-pdf me-1' />
                                      Syllabus
                                    </a>
                                  ) : (
                                    <span className='text-muted small'>
                                      No syllabus
                                    </span>
                                  )}
                                </div>
                              </div>
                            </AccordionSummary>

                            <AccordionDetails>
                              {subChapters.length === 0 ? (
                                <div className='alert alert-light border-0 bg-white text-center py-3 mb-0'>
                                  <small className='text-muted'>
                                    No chapters found for this subject.
                                  </small>
                                </div>
                              ) : (
                                <div className='ps-1'>
                                  {subChapters.map((ch, cIndex) => {
                                    const chId = ch?.chapter_id;
                                    const chKey = `ch-${subKey}-${chId || cIndex}`;
                                    const topics =
                                      topicsByChapter.get(chId) || [];

                                    return (
                                      <Accordion
                                        key={chKey}
                                        expanded={expandedCh === chKey}
                                        onChange={(_, isExpanded) =>
                                          setExpandedCh(
                                            isExpanded ? chKey : false,
                                          )
                                        }
                                        className='mb-2'
                                      >
                                        <AccordionSummary
                                          expandIcon={<ExpandMoreIcon />}
                                          className='bg-white'
                                        >
                                          <div className='d-flex justify-content-between align-items-center w-100 pe-2'>
                                            <div>
                                              <Typography className='fw-semibold'>
                                                Chapter{' '}
                                                {ch?.chapter_order ||
                                                  cIndex + 1}
                                                :{' '}
                                                {ch?.chapter_name ||
                                                  'Untitled Chapter'}
                                              </Typography>
                                              <Typography
                                                variant='caption'
                                                className='text-muted'
                                              >
                                                ChapterId: {chId || 'N/A'} •
                                                Topics: {topics.length}
                                              </Typography>
                                            </div>

                                            <Chip
                                              label={`${topics.length} Topic${topics.length !== 1 ? 's' : ''}`}
                                              size='small'
                                              variant='outlined'
                                            />
                                          </div>
                                        </AccordionSummary>

                                        <AccordionDetails>
                                          {topics.length === 0 ? (
                                            <div className='alert alert-light border-0 bg-white text-center py-3 mb-0'>
                                              <small className='text-muted'>
                                                No topics/material found for
                                                this chapter.
                                              </small>
                                            </div>
                                          ) : (
                                            <div className='table-responsive'>
                                              <table className='table table-sm table-striped align-middle mb-0'>
                                                <thead>
                                                  <tr>
                                                    <th style={{ width: 70 }}>
                                                      #
                                                    </th>
                                                    <th style={{ width: 140 }}>
                                                      Code
                                                    </th>
                                                    <th>Topic</th>
                                                    <th style={{ width: 140 }}>
                                                      File
                                                    </th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {topics.map((t, ti) => (
                                                    <tr
                                                      key={t?.material_id || ti}
                                                    >
                                                      <td>{ti + 1}</td>
                                                      <td>
                                                        {t?.topic_code || ''}
                                                      </td>
                                                      <td>
                                                        {t?.topic_name || ''}
                                                      </td>
                                                      <td>
                                                        {t?.file_url ? (
                                                          <a
                                                            href={t.file_url}
                                                            target='_blank'
                                                            rel='noreferrer'
                                                            className='btn btn-sm btn-outline-secondary'
                                                          >
                                                            <i className='bi bi-box-arrow-up-right me-1' />
                                                            Open
                                                          </a>
                                                        ) : (
                                                          <span className='text-muted small'>
                                                            No file
                                                          </span>
                                                        )}
                                                      </td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </div>
                                          )}
                                        </AccordionDetails>
                                      </Accordion>
                                    );
                                  })}
                                </div>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </div>
            )}
          </div>

          <div className='card-footer bg-light border-top py-3'>
            <div className='d-flex justify-content-between align-items-center'>
              <div className='text-muted small'>
                Read-only view • Source: Sem → Subject → Chapter → Topics
              </div>
              <button
                type='button'
                className='btn btn-outline-secondary'
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                <i className='bi bi-arrow-up me-2' />
                Top
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CourseContent;
